import { useState } from 'react';

import { ApiClientError } from '../../api/client.js';
import {
  useChannels,
  useCreateChannel,
  useDeleteChannel,
  useExportChannel,
  usePartnerLinks,
  usePartnerProviders,
  usePartnerShops,
  type MissingCredentialField,
  type PartnerLinkRow,
  useSetupChannel,
  useTestChannel,
  useTestChannelCredentials,
  useStores,
  useUpdateChannel,
  type ChannelRow,
} from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import { StoreSelect } from '../../components/store-select.js';
import {
  Button,
  ChannelIcon,
  type ChannelType,
  ConfirmModal,
  DrawerPanel,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  Toggle,
  channelLabel,
} from '../../components/ui/index.js';

// Чат на сайте здесь НЕ показываем — этот канал создаётся автоматически при
// первом сообщении из виджета; встройка живёт в разделе «Виджеты».
const ALL_CHANNELS: { type: ChannelType; desc: string }[] = [
  { type: 'telegram', desc: 'Бот через @BotFather' },
  { type: 'whatsapp', desc: 'WhatsApp Business (Meta)' },
  { type: 'vk', desc: 'Сообщество VK · Callback API' },
  { type: 'email', desc: 'Приём по IMAP · отправка по SMTP (или Mailgun)' },
  { type: 'sms', desc: 'SMS.ru · ответы на телефон из формы' },
  { type: 'wildberries', desc: 'Отзывы и вопросы товаров' },
  { type: 'ozon', desc: 'Личный кабинет селлера' },
  { type: 'avito', desc: 'OAuth, сообщения и вопросы' },
];

const STEPS_COUNT: Partial<Record<ChannelType, number>> = {
  telegram: 3,
  whatsapp: 4,
  vk: 3,
  email: 3,
  sms: 2,
  wildberries: 3,
  ozon: 3,
  avito: 2,
};

/** Редактируемые поля доступов по типу канала (для формы редактирования). */
const CRED_FIELDS: Partial<
  Record<ChannelType, Array<{ key: string; label: string; password?: boolean }>>
> = {
  telegram: [{ key: 'bot_token', label: 'Bot Token', password: true }],
  vk: [
    { key: 'access_token', label: 'Ключ доступа сообщества', password: true },
    { key: 'secret', label: 'Секретный ключ' },
    { key: 'confirmation_string', label: 'Строка подтверждения (необязательно)' },
  ],
  whatsapp: [
    { key: 'access_token', label: 'Access Token', password: true },
    { key: 'app_secret', label: 'App Secret', password: true },
    { key: 'phone_number_id', label: 'Phone Number ID' },
  ],
  email: [
    { key: 'imap_host', label: 'IMAP-хост' },
    { key: 'imap_port', label: 'IMAP-порт' },
    { key: 'imap_user', label: 'IMAP логин' },
    { key: 'imap_pass', label: 'IMAP пароль', password: true },
    { key: 'smtp_host', label: 'SMTP-хост' },
    { key: 'smtp_port', label: 'SMTP-порт' },
    { key: 'smtp_user', label: 'SMTP логин' },
    { key: 'smtp_pass', label: 'SMTP пароль', password: true },
    { key: 'smtp_from', label: 'Email отправителя' },
  ],
  wildberries: [{ key: 'api_key', label: 'API-ключ', password: true }],
  ozon: [
    { key: 'api_key', label: 'API-ключ', password: true },
    { key: 'client_id', label: 'Client-ID' },
  ],
  avito: [
    { key: 'client_id', label: 'Client ID' },
    { key: 'client_secret', label: 'Client Secret', password: true },
    { key: 'user_id', label: 'User ID' },
  ],
  sms: [
    { key: 'api_id', label: 'api_id', password: true },
    { key: 'from', label: 'Имя отправителя' },
  ],
};

/** Приводит строковые поля мастера к типам, которые ждёт бэкенд-адаптер. */
function normalizeCreds(
  type: ChannelType,
  creds: Record<string, string>,
): Record<string, unknown> {
  if (type === 'email') {
    const num = (v?: string): number | undefined => (v ? Number(v) : undefined);
    const bool = (v?: string): boolean | undefined =>
      v === '1' ? true : v === '0' ? false : undefined;
    return {
      ...creds,
      imap_port: num(creds.imap_port),
      smtp_port: num(creds.smtp_port),
      imap_secure: bool(creds.imap_secure),
      smtp_secure: bool(creds.smtp_secure),
    };
  }
  return creds;
}

/** Кнопка реальной проверки credentials до создания канала + показ результата. */
function TestConnectionButton({
  type,
  creds,
  label = 'Проверить подключение',
}: {
  type: ChannelType;
  creds: Record<string, string>;
  label?: string;
}): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const test = useTestChannelCredentials(user?.workspaceSlug);
  const res = test.data;
  return (
    <div style={{ marginTop: 4 }}>
      <Button
        variant="secondary"
        icon="ti-check"
        disabled={test.isPending}
        onClick={() => test.mutate({ type, credentials: normalizeCreds(type, creds) })}
      >
        {test.isPending ? 'Проверка…' : label}
      </Button>
      {(res || test.isError) && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: res?.ok ? 'var(--inbx-success, #16a34a)' : 'var(--inbx-danger, #dc2626)',
          }}
        >
          {res?.ok
            ? `✓ Подключение успешно${
                res.details?.balance != null ? ` · баланс: ${String(res.details.balance)}` : ''
              }`
            : `✕ ${res?.error ?? 'не удалось проверить'}`}
        </div>
      )}
      {res?.ok && type === 'vk' && (
        <div
          style={{
            marginTop: 6,
            fontSize: 13,
            color: res.details?.manage
              ? 'var(--inbx-success, #16a34a)'
              : 'var(--inbx-text-secondary, #6b7280)',
          }}
        >
          {res.details?.manage
            ? '✓ У ключа есть право «Управление» — Callback API настроим автоматически при сохранении, делать ничего не нужно.'
            : '⚠ У ключа нет права «Управление» — после «Завершить» настройте Callback вручную (адрес сервера покажем после создания канала).'}
        </div>
      )}
    </div>
  );
}

export function SettingsChannels(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const channelsQ = useChannels(slug);
  const list = channelsQ.data ?? [];
  const [wizardType, setWizardType] = useState<ChannelType | null>(null);
  const [editChannel, setEditChannel] = useState<ChannelRow | null>(null);

  return (
    <div className="inbx-page">
      <PageHeader title="Каналы связи" badge={{ count: list.length }} />
      <div className="inbx-page__body">
        <h4 style={{ margin: '0 0 12px' }}>Подключённые</h4>
        <div className="inbx-grid-3">
          {list.map((c) => (
            <ChannelCard key={c.id} channel={c} onOpen={() => setEditChannel(c)} />
          ))}
        </div>

        <h4 style={{ margin: '24px 0 12px' }}>Добавить канал</h4>
        <div className="inbx-grid-3">
          {/* Не исключаем уже подключённые типы: можно подключить несколько ботов/
              ящиков/сообществ одного типа — каждый станет отдельным каналом. */}
          {ALL_CHANNELS.map((c) => (
            <div
              key={c.type}
              className="inbx-card"
              style={{ display: 'flex', gap: 12, alignItems: 'center' }}
            >
              <ChannelIcon channel={c.type} size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{channelLabel(c.type)}</div>
                <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
                  {c.desc}
                </div>
              </div>
              <Button size="sm" variant="primary" onClick={() => setWizardType(c.type)}>
                Подключить
              </Button>
            </div>
          ))}
        </div>
      </div>

      {wizardType && <ChannelWizard type={wizardType} onClose={() => setWizardType(null)} />}
      {editChannel && (
        <ChannelEditDrawer channel={editChannel} onClose={() => setEditChannel(null)} />
      )}
    </div>
  );
}

function ChannelCard({
  channel,
  onOpen,
}: {
  channel: ChannelRow;
  onOpen: () => void;
}): JSX.Element {
  const ch = channel.type as ChannelType;
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  const storeName = (useStores(slug).data ?? []).find((s) => s.id === channel.store_id)?.name;
  const status = channel.is_active ? 'Подключён' : 'Не активен';
  return (
    <div className="inbx-card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <ChannelIcon channel={ch} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {channel.name}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
          <span
            className={`inbx-pill-badge inbx-pill-badge--${channel.is_active ? 'success' : 'default'}`}
          >
            {status}
          </span>
          {storeName && <span className="inbx-pill-badge inbx-pill-badge--default">{storeName}</span>}
          {channel.type === 'partner' && channel.config?.can_reply === false && (
            <span className="inbx-pill-badge inbx-pill-badge--warning" title={String(channel.config.reply_hint ?? '')}>
              только чтение
            </span>
          )}
        </div>
      </div>
      <IconButton icon="ti-settings" tooltip="Настройки" onClick={onOpen} />
    </div>
  );
}

function ChannelEditDrawer({
  channel,
  onClose,
}: {
  channel: ChannelRow;
  onClose: () => void;
}): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const update = useUpdateChannel(user?.workspaceSlug);
  const del = useDeleteChannel(user?.workspaceSlug);
  const test = useTestChannel(user?.workspaceSlug);
  const tr = test.data;
  const setup = useSetupChannel(user?.workspaceSlug);
  const [confirmDel, setConfirmDel] = useState(false);
  const type = channel.type as ChannelType;
  const [name, setName] = useState(channel.name);
  const [isActive, setIsActive] = useState(channel.is_active);
  const [storeId, setStoreId] = useState<string | null>(channel.store_id ?? null);
  const cfg = channel.config ?? {};
  const [recvEnabled, setRecvEnabled] = useState(cfg.recv_enabled !== false);
  const [sendEnabled, setSendEnabled] = useState(cfg.send_enabled !== false);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const fields = CRED_FIELDS[type] ?? [];

  async function save(): Promise<void> {
    const patch: Record<string, unknown> = { name, is_active: isActive };
    if (!cfg.partner_link_id) patch.store_id = storeId;
    if (type === 'email') {
      patch.config = { ...cfg, recv_enabled: recvEnabled, send_enabled: sendEnabled };
    }
    // только заполненные поля доступов (merge на бэкенде не сотрёт остальные)
    const credPatch: Record<string, unknown> = {};
    for (const f of fields) {
      const v = (creds[f.key] ?? '').trim();
      if (v === '') continue;
      credPatch[f.key] = f.key.endsWith('_port') ? Number(v) : v;
    }
    if (Object.keys(credPatch).length > 0) patch.credentials = credPatch;
    try {
      await update.mutateAsync({ id: channel.id, patch });
      onClose();
    } catch {
      /* ошибка отрисуется баннером ниже */
    }
  }

  return (
    <DrawerPanel
      open
      title={`Канал: ${channel.name}`}
      subtitle={channelLabel(type)}
      onClose={onClose}
      width={520}
      footer={
        <>
          <Button variant="danger" onClick={() => setConfirmDel(true)}>
            Удалить
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      {update.isError && (
        <div
          style={{
            background: 'var(--inbx-bg-secondary)',
            border: '1px solid var(--inbx-danger, #dc2626)',
            color: 'var(--inbx-danger, #dc2626)',
            borderRadius: 'var(--inbx-radius)',
            padding: 12,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          ✕ {(update.error as Error)?.message ?? 'Не удалось сохранить'}
        </div>
      )}
      <Field label="Название">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <StoreSelect value={storeId} onChange={setStoreId} locked={Boolean(cfg.partner_link_id)} />
      {type === 'partner' && (
        <div style={{ color: 'var(--inbx-text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          Канал магазина на платформе: сообщения принимает платформа и пересылает сюда, ответы
          уходят через неё. Ключи и настройки канала — в кабинете платформы.
          {cfg.can_reply === false && (
            <div style={{ marginTop: 6, color: 'var(--inbx-warning, #d97706)' }}>
              Ответить пока нельзя. {String(cfg.reply_hint ?? '')}
            </div>
          )}
        </div>
      )}
      <Toggle checked={isActive} onChange={setIsActive} label="Канал активен" />
      {type === 'email' && (
        <>
          <div style={{ height: 8 }} />
          <Toggle checked={recvEnabled} onChange={setRecvEnabled} label="Принимать входящие" />
          <div style={{ height: 8 }} />
          <Toggle checked={sendEnabled} onChange={setSendEnabled} label="Отправлять ответы" />
        </>
      )}
      {fields.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="inbx-editor-section__title" style={{ marginBottom: 4 }}>
            Доступы
          </div>
          <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)', marginBottom: 10 }}>
            Заполняйте только то, что хотите изменить. Пустые поля останутся прежними.
          </div>
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              <Input
                type={f.password ? 'password' : 'text'}
                placeholder="не менять"
                value={creds[f.key] ?? ''}
                onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
              />
            </Field>
          ))}
          {/* Проверяем СОХРАНЁННЫЕ доступы канала (не пустые поля формы). */}
          <div style={{ marginTop: 4 }}>
            <Button
              variant="secondary"
              icon="ti-check"
              disabled={test.isPending}
              onClick={() => test.mutate(channel.id)}
            >
              {test.isPending ? 'Проверка…' : 'Проверить подключение'}
            </Button>
            {(tr || test.isError) && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: tr?.ok ? 'var(--inbx-success, #16a34a)' : 'var(--inbx-danger, #dc2626)',
                }}
              >
                {tr?.ok ? '✓ Подключение работает' : `✕ ${tr?.error ?? 'не удалось проверить'}`}
              </div>
            )}
            {tr?.ok && type === 'vk' && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color: tr.details?.manage
                    ? 'var(--inbx-success, #16a34a)'
                    : 'var(--inbx-text-secondary, #6b7280)',
                }}
              >
                {tr.details?.manage
                  ? '✓ Есть право «Управление» — можно настроить Callback кнопкой ниже.'
                  : '⚠ Нет права «Управление» — Callback нужно настроить вручную в сообществе.'}
              </div>
            )}
          </div>
          {type === 'vk' && (
            <div style={{ marginTop: 10 }}>
              <Button
                variant="primary"
                icon="ti-plug"
                disabled={setup.isPending}
                onClick={() => setup.mutate(channel.id)}
              >
                {setup.isPending ? 'Настройка в VK…' : 'Настроить Callback в VK'}
              </Button>
              {setup.data && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: setup.data.ok
                      ? 'var(--inbx-success, #16a34a)'
                      : 'var(--inbx-danger, #dc2626)',
                  }}
                >
                  {setup.data.ok
                    ? '✓ Callback зарегистрирован и подтверждён в VK — входящие пойдут в тикеты.'
                    : `✕ Не подтверждён (статус: ${setup.data.status}${
                        setup.data.error ? ', ' + setup.data.error : ''
                      }).`}
                </div>
              )}
              {setup.isError && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--inbx-danger, #dc2626)' }}>
                  ✕ {(setup.error as Error)?.message ?? 'ошибка настройки'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <PartnerExport channel={channel} />
      <ConfirmModal
        open={confirmDel}
        title="Удалить канал?"
        description={`Канал «${channel.name}» будет удалён, входящие из него перестанут поступать. Действие необратимо.`}
        confirmLabel="Удалить"
        variant="danger"
        onCancel={() => setConfirmDel(false)}
        onConfirm={() => {
          del.mutate(channel.id, { onSuccess: onClose });
          setConfirmDel(false);
        }}
      />
    </DrawerPanel>
  );
}

/**
 * «Передать в магазин» — ключ этого канала уезжает в связанную платформу,
 * чтобы продавец не вводил его там второй раз.
 *
 * Показывается только для платформ, которые такие ключи принимают, и только
 * администратору (сервер это проверяет). Перед отправкой — явное
 * подтверждение с тем, ЧТО и КУДА уйдёт: это передача секрета.
 */
function PartnerExport({ channel }: { channel: ChannelRow }): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const providersQ = usePartnerProviders(slug);
  const linksQ = usePartnerLinks(slug);
  const accepting = new Map(
    (providersQ.data ?? []).map((p) => [p.id, { title: p.title, types: p.accepts_channel_types }]),
  );
  const targets = (linksQ.data ?? []).filter(
    (l) => l.status === 'active' && accepting.get(l.provider)?.types.includes(channel.type),
  );
  // Ключ пришёл ОТТУДА же — отправлять его обратно бессмысленно.
  const cameFrom = (channel.config ?? {}).partner_link_id as string | undefined;
  const available = targets.filter((l) => l.id !== cameFrom);
  if (available.length === 0 || user?.role === 'operator' || user?.role === 'supervisor') {
    return null;
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div className="inbx-editor-section__title" style={{ marginBottom: 4 }}>
        Передать в другой сервис
      </div>
      <div
        style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)', marginBottom: 10 }}
      >
        Чтобы не вводить этот ключ второй раз. Он уйдёт с сервера на сервер и будет проверен на той
        стороне.
      </div>
      {available.map((link) => (
        <PartnerExportRow
          key={link.id}
          link={link}
          channel={channel}
          title={accepting.get(link.provider)?.title ?? link.provider}
        />
      ))}
    </div>
  );
}

const MARKETPLACE_CHANNELS = new Set(['ozon', 'wildberries', 'avito']);

function PartnerExportRow(props: {
  link: PartnerLinkRow;
  channel: ChannelRow;
  title: string;
}): JSX.Element {
  const slug = useAuthStore((s) => s.user?.workspaceSlug);
  // Маркетплейс у платформы живёт в конкретном магазине — спрашиваем, в каком.
  const needsShop = MARKETPLACE_CHANNELS.has(props.channel.type);
  const shopsQ = usePartnerShops(needsShop ? slug : undefined, props.link.id);
  const exportChannel = useExportChannel(slug);
  const [shopId, setShopId] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const shops = shopsQ.data ?? [];
  const chosenShop = needsShop ? (shopId || shops[0]?.id) : undefined;
  const shopName = shops.find((s) => s.id === chosenShop)?.name;
  const target = props.link.externalAccountName ?? props.title;

  function run(): void {
    setResult(null);
    exportChannel.mutate(
      { linkId: props.link.id, channelId: props.channel.id, shopId: chosenShop },
      {
        onSuccess: (r) =>
          setResult({ ok: true, text: r.created ? 'Передано и проверено' : 'Обновлено на той стороне' }),
        onError: (err) => {
          const missing =
            err instanceof ApiClientError
              ? ((err.details as { missing?: MissingCredentialField[] } | undefined)?.missing ?? [])
              : [];
          setResult({
            ok: false,
            text: missing.length
              ? `Не хватает: ${missing.map((m) => m.label).join(', ')} — заполните в «Доступах» выше`
              : err instanceof Error
                ? err.message
                : 'не удалось передать',
          });
        },
      },
    );
  }

  return (
    <div className="inbx-card" style={{ padding: 10, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>{target}</span>
        <Button
          size="sm"
          variant="secondary"
          disabled={exportChannel.isPending || (needsShop && !chosenShop)}
          onClick={() => setConfirm(true)}
        >
          {exportChannel.isPending ? 'Передаём…' : 'Передать'}
        </Button>
      </div>
      {needsShop && (
        <Field label="В магазин">
          <Select value={chosenShop ?? ''} onChange={(e) => setShopId(e.target.value)}>
            {shopsQ.isLoading && <option>Загружаем…</option>}
            {shops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {shopsQ.isError && (
        <div style={{ fontSize: 13, color: 'var(--inbx-danger, #dc2626)' }}>
          ✕ {(shopsQ.error as Error)?.message ?? 'не удалось получить список магазинов'}
        </div>
      )}
      {result && (
        <div
          style={{
            fontSize: 13,
            color: result.ok ? 'var(--inbx-success, #16a34a)' : 'var(--inbx-danger, #dc2626)',
          }}
        >
          {result.ok ? '✓' : '✕'} {result.text}
        </div>
      )}
      <ConfirmModal
        open={confirm}
        title="Передать ключ канала?"
        description={`Ключ канала «${props.channel.name}» будет передан в «${target}»${
          shopName ? `, магазин «${shopName}»` : ''
        }. Там его проверят и сохранят. Отозвать можно, удалив подключение на той стороне.`}
        confirmLabel="Передать"
        onCancel={() => setConfirm(false)}
        onConfirm={() => {
          setConfirm(false);
          run();
        }}
      />
    </div>
  );
}

function ChannelWizard({ type, onClose }: { type: ChannelType; onClose: () => void }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const createChannel = useCreateChannel(user?.workspaceSlug);
  const [step, setStep] = useState(1);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [channelName, setChannelName] = useState(channelLabel(type));
  const [storeId, setStoreId] = useState<string | null>(null);
  const stepsCount = STEPS_COUNT[type] ?? 3;

  async function handleFinish(): Promise<void> {
    try {
      await createChannel.mutateAsync({
        type,
        name: channelName,
        credentials: normalizeCreds(type, creds),
        config,
        store_id: storeId,
      });
      onClose();
    } catch {
      // Ошибку показываем баннером в теле мастера (createChannel.error). Drawer не закрываем.
    }
  }

  return (
    <DrawerPanel
      open
      title={`Подключение: ${channelLabel(type)}`}
      subtitle={`Шаг ${step} из ${stepsCount}`}
      onClose={onClose}
      width={520}
      footer={
        <>
          {step > 1 && (
            <Button variant="secondary" onClick={() => setStep(step - 1)}>
              ← Назад
            </Button>
          )}
          {step < stepsCount ? (
            <Button variant="primary" onClick={() => setStep(step + 1)}>
              Далее →
            </Button>
          ) : (
            <Button variant="primary" onClick={handleFinish} disabled={createChannel.isPending}>
              {createChannel.isPending ? 'Подключение…' : 'Завершить'}
            </Button>
          )}
        </>
      }
    >
      {createChannel.isError && (
        <div
          style={{
            background: 'var(--inbx-bg-secondary)',
            border: '1px solid var(--inbx-danger, #dc2626)',
            color: 'var(--inbx-danger, #dc2626)',
            borderRadius: 'var(--inbx-radius)',
            padding: 12,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          ✕ {(createChannel.error as Error)?.message ?? 'Не удалось подключить канал'}
        </div>
      )}
      {step === stepsCount && <StoreSelect value={storeId} onChange={setStoreId} />}
      {type === 'telegram' && (
        <TelegramWizard
          step={step}
          creds={creds}
          setCreds={setCreds}
          channelName={channelName}
          setChannelName={setChannelName}
        />
      )}
      {type === 'whatsapp' && <WhatsappWizard step={step} creds={creds} setCreds={setCreds} />}
      {type === 'vk' && <VkWizard step={step} creds={creds} setCreds={setCreds} />}
      {type === 'email' && (
        <EmailWizard
          step={step}
          creds={creds}
          setCreds={setCreds}
          config={config}
          setConfig={setConfig}
          channelName={channelName}
          setChannelName={setChannelName}
        />
      )}
      {type === 'sms' && (
        <SmsWizard
          step={step}
          creds={creds}
          setCreds={setCreds}
          channelName={channelName}
          setChannelName={setChannelName}
        />
      )}
      {type === 'wildberries' && (
        <MarketplaceWizard step={step} platform="Wildberries" creds={creds} setCreds={setCreds} />
      )}
      {type === 'ozon' && (
        <MarketplaceWizard step={step} platform="Ozon" creds={creds} setCreds={setCreds} />
      )}
      {type === 'avito' && <AvitoWizard step={step} creds={creds} setCreds={setCreds} />}
    </DrawerPanel>
  );
}

function TelegramWizard({
  step,
  creds,
  setCreds,
  channelName,
  setChannelName,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
  channelName: string;
  setChannelName: (n: string) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>Создание бота</h4>
        <ol style={{ lineHeight: 1.7 }}>
          <li>Открой @BotFather в Telegram</li>
          <li>
            Отправь <code>/newbot</code>
          </li>
          <li>
            Задай имя и username (должен заканчиваться на <code>_bot</code>)
          </li>
          <li>
            Скопируй <strong>Bot Token</strong> который пришлёт BotFather
          </li>
        </ol>
        <Button
          variant="secondary"
          icon="ti-external-link"
          onClick={() => window.open('https://t.me/BotFather', '_blank', 'noopener,noreferrer')}
        >
          Открыть @BotFather
        </Button>
      </>
    );
  if (step === 2)
    return (
      <>
        <h4>API Token</h4>
        <Field label="Bot Token" required hint="формат: 123456789:ABCdef...">
          <Input
            type="password"
            placeholder="123456789:AAEhBP..."
            value={creds.bot_token ?? ''}
            onChange={(e) => setCreds({ ...creds, bot_token: e.target.value })}
          />
        </Field>
        <Field label="Название (для отображения)" required>
          <Input
            placeholder="Поддержка Demo"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
          />
        </Field>
        <TestConnectionButton type="telegram" creds={creds} label="Проверить токен" />
      </>
    );
  return (
    <>
      <h4>Подтверждение</h4>
      <ol style={{ lineHeight: 1.7 }}>
        <li>
          Нажми <strong>«Завершить»</strong> внизу — это создаст канал и подключит вебхук
          (бот начнёт принимать сообщения).
        </li>
        <li>Напиши боту любое сообщение в Telegram.</li>
        <li>
          Открой раздел <strong>«Входящие»</strong> — там появится новый тикет (без перезагрузки).
        </li>
      </ol>
      <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
        Имя бота тут — только подпись для интерфейса, на связь не влияет. Если подключение
        не удастся, причина появится прямо в этом окне.
      </p>
    </>
  );
}

function WhatsappWizard({
  step,
  creds,
  setCreds,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>Требования</h4>
        <ul style={{ lineHeight: 1.8 }}>
          <li>☐ Facebook Business аккаунт</li>
          <li>☐ Верифицированный номер телефона</li>
          <li>☐ Приложение в Meta for Developers</li>
        </ul>
      </>
    );
  if (step === 2)
    return (
      <>
        <h4>Meta App credentials</h4>
        <Field label="Phone Number ID" required>
          <Input
            value={creds.phone_number_id ?? ''}
            onChange={(e) => setCreds({ ...creds, phone_number_id: e.target.value })}
          />
        </Field>
        <Field label="Business Account ID" required>
          <Input
            value={creds.business_account_id ?? ''}
            onChange={(e) => setCreds({ ...creds, business_account_id: e.target.value })}
          />
        </Field>
        <Field label="Access Token" required>
          <Input
            type="password"
            value={creds.access_token ?? ''}
            onChange={(e) => setCreds({ ...creds, access_token: e.target.value })}
          />
        </Field>
        <Field label="App Secret" required hint="для проверки подписи webhook">
          <Input
            type="password"
            value={creds.app_secret ?? ''}
            onChange={(e) => setCreds({ ...creds, app_secret: e.target.value })}
          />
        </Field>
        <TestConnectionButton type="whatsapp" creds={creds} />
      </>
    );
  if (step === 3)
    return (
      <>
        <h4>Настройка webhook в Meta Developer Console</h4>
        <Field label="Webhook URL">
          <Input readOnly value="https://inbx.app/webhook/whatsapp/demo" />
        </Field>
        <Field label="Verify Token">
          <Input readOnly value="inbx_wh_d3kf9j2" />
        </Field>
        <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          Подпишись на события: messages, message_deliveries, message_reads.
        </p>
      </>
    );
  return (
    <>
      <h4>Проверка</h4>
      <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
        Проверим доступ к WhatsApp Business API с указанными данными.
      </p>
      <TestConnectionButton type="whatsapp" creds={creds} />
    </>
  );
}

function VkWizard({
  step,
  creds,
  setCreds,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>Сообщество VK</h4>
        <Field label="ID сообщества" required>
          <Input
            placeholder="123456"
            value={creds.group_id ?? ''}
            onChange={(e) => setCreds({ ...creds, group_id: e.target.value })}
          />
        </Field>
        <Field label="Ключ доступа сообщества" required>
          <Input
            type="password"
            value={creds.access_token ?? ''}
            onChange={(e) => setCreds({ ...creds, access_token: e.target.value })}
          />
        </Field>
        <TestConnectionButton type="vk" creds={creds} />
      </>
    );
  if (step === 2)
    return (
      <>
        <h4>Callback API</h4>
        <p style={{ fontSize: 'var(--inbx-fz-sm)' }}>
          Задайте «Секретный ключ» (любой) и нажмите «Завершить».
        </p>
        <div
          style={{
            background: 'var(--inbx-bg-secondary)',
            borderRadius: 'var(--inbx-radius)',
            padding: 10,
            fontSize: 'var(--inbx-fz-sm)',
            lineHeight: 1.6,
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <strong>Если у ключа доступа есть право «Управление»</strong> — мы настроим Callback API
            сами, больше делать ничего не нужно.
          </div>
          <div>
            <strong>Если права нет</strong> — настройте вручную: сообщество → Управление → «Работа
            с API» → Callback API → вставьте адрес сервера{' '}
            <code>…/webhook/channel/vk/&lt;id&gt;</code> (покажется после создания) и тот же секрет →
            «Подтвердить» (код подтверждения отдаём автоматически) → включите событие «Входящие
            сообщения».
          </div>
        </div>
        <Field label="Секретный ключ" hint="придумайте сами; одинаково здесь и в VK">
          <Input
            value={creds.secret ?? ''}
            onChange={(e) => setCreds({ ...creds, secret: e.target.value })}
          />
        </Field>
        <Field
          label="Строка подтверждения (необязательно)"
          hint="обычно не нужна — система берёт код из VK сама; заполните, только если VK её не принимает"
        >
          <Input
            placeholder="a3f9k2…"
            value={creds.confirmation_string ?? ''}
            onChange={(e) => setCreds({ ...creds, confirmation_string: e.target.value })}
          />
        </Field>
      </>
    );
  if (step === 3)
    return (
      <>
        <h4>Готово</h4>
        <p style={{ lineHeight: 1.6 }}>
          Нажмите <strong>«Завершить»</strong>. Мы собираем <strong>входящие сообщения</strong>{' '}
          сообщества (событие <code>message_new</code>) — каждое станет тикетом во «Входящих».
        </p>
        <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          Комментарии к постам и упоминания пока не обрабатываются — поэтому их тут и нет.
          Если у ключа есть право «Управление», подписка на сообщения включится автоматически;
          иначе включите её вручную в Callback API сообщества.
        </p>
      </>
    );
  return <></>;
}

function EmailWizard({
  step,
  creds,
  setCreds,
  config,
  setConfig,
  channelName,
  setChannelName,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
  config: Record<string, unknown>;
  setConfig: (c: Record<string, unknown>) => void;
  channelName: string;
  setChannelName: (n: string) => void;
}): JSX.Element {
  const recvMode = creds.recv_mode ?? 'imap';
  // SSL по умолчанию включён (undefined трактуем как «вкл»).
  const sslOn = (key: string): boolean => creds[key] !== '0';
  const setSsl = (key: string, v: boolean): void => setCreds({ ...creds, [key]: v ? '1' : '0' });

  if (step === 1)
    return (
      <>
        <h4>Входящие (приём писем)</h4>
        <Field label="Название канала" required>
          <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} />
        </Field>
        <Field label="Способ приёма">
          <Select
            value={recvMode}
            onChange={(e) => setCreds({ ...creds, recv_mode: e.target.value })}
          >
            <option value="imap">IMAP (обычный ящик)</option>
            <option value="mailgun">Mailgun (webhook)</option>
          </Select>
        </Field>

        {recvMode === 'imap' ? (
          <>
            <div className="inbx-form-row">
              <Field label="IMAP-хост" required>
                <Input
                  placeholder="imap.yandex.ru"
                  value={creds.imap_host ?? ''}
                  onChange={(e) => setCreds({ ...creds, imap_host: e.target.value })}
                />
              </Field>
              <Field label="Порт">
                <Input
                  type="number"
                  placeholder="993"
                  value={creds.imap_port ?? ''}
                  onChange={(e) => setCreds({ ...creds, imap_port: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Логин (обычно полный email)" required>
              <Input
                placeholder="support@yandex.ru"
                value={creds.imap_user ?? ''}
                onChange={(e) => setCreds({ ...creds, imap_user: e.target.value })}
              />
            </Field>
            <Field label="Пароль" required hint="для Яндекс/Gmail — пароль приложения">
              <Input
                type="password"
                value={creds.imap_pass ?? ''}
                onChange={(e) => setCreds({ ...creds, imap_pass: e.target.value })}
              />
            </Field>
            <Field label="Папка">
              <Input
                placeholder="INBOX"
                value={creds.imap_folder ?? ''}
                onChange={(e) => setCreds({ ...creds, imap_folder: e.target.value })}
              />
            </Field>
            <Toggle
              checked={sslOn('imap_secure')}
              onChange={(v) => setSsl('imap_secure', v)}
              label="SSL (порт 993)"
            />
          </>
        ) : (
          <>
            <Field label="Mailgun API key" required>
              <Input
                type="password"
                value={creds.mailgun_api_key ?? ''}
                onChange={(e) => setCreds({ ...creds, mailgun_api_key: e.target.value })}
              />
            </Field>
            <Field label="Mailgun домен" required>
              <Input
                placeholder="mg.example.com"
                value={creds.mailgun_domain ?? ''}
                onChange={(e) => setCreds({ ...creds, mailgun_domain: e.target.value })}
              />
            </Field>
            <Field label="Signing key" hint="для проверки подписи webhook">
              <Input
                type="password"
                value={creds.mailgun_signing_key ?? ''}
                onChange={(e) => setCreds({ ...creds, mailgun_signing_key: e.target.value })}
              />
            </Field>
          </>
        )}
        <div style={{ height: 8 }} />
        <TestConnectionButton type="email" creds={creds} />
      </>
    );

  if (step === 2)
    return (
      <>
        <h4>Исходящие (отправка по SMTP)</h4>
        <Button
          variant="secondary"
          icon="ti-copy"
          onClick={() =>
            setCreds({
              ...creds,
              smtp_host: creds.smtp_host || creds.imap_host?.replace(/^imap\./, 'smtp.') || '',
              smtp_port: creds.smtp_port || '465',
              smtp_user: creds.smtp_user || creds.imap_user || '',
              smtp_pass: creds.smtp_pass || creds.imap_pass || '',
              smtp_from: creds.smtp_from || creds.imap_user || '',
            })
          }
        >
          Заполнить из входящего ящика
        </Button>
        <div style={{ height: 12 }} />
        <Field label="Имя отправителя">
          <Input
            placeholder="Поддержка магазина"
            value={creds.smtp_from_name ?? ''}
            onChange={(e) => setCreds({ ...creds, smtp_from_name: e.target.value })}
          />
        </Field>
        <Field label="Email отправителя" required>
          <Input
            type="email"
            placeholder="support@yandex.ru"
            value={creds.smtp_from ?? ''}
            onChange={(e) => setCreds({ ...creds, smtp_from: e.target.value })}
          />
        </Field>
        <div className="inbx-form-row">
          <Field label="SMTP-хост" required>
            <Input
              placeholder="smtp.yandex.ru"
              value={creds.smtp_host ?? ''}
              onChange={(e) => setCreds({ ...creds, smtp_host: e.target.value })}
            />
          </Field>
          <Field label="Порт">
            <Input
              type="number"
              placeholder="465"
              value={creds.smtp_port ?? ''}
              onChange={(e) => setCreds({ ...creds, smtp_port: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Логин">
          <Input
            value={creds.smtp_user ?? ''}
            onChange={(e) => setCreds({ ...creds, smtp_user: e.target.value })}
          />
        </Field>
        <Field label="Пароль">
          <Input
            type="password"
            value={creds.smtp_pass ?? ''}
            onChange={(e) => setCreds({ ...creds, smtp_pass: e.target.value })}
          />
        </Field>
        <Toggle
          checked={sslOn('smtp_secure')}
          onChange={(v) => setSsl('smtp_secure', v)}
          label="SSL (порт 465; для 587 выключите)"
        />
        <div style={{ height: 8 }} />
        <TestConnectionButton type="email" creds={creds} />
      </>
    );

  // step 3 — активность
  return (
    <>
      <h4>Активность</h4>
      <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
        Можно включить только приём, только отправку или и то и другое.
      </p>
      <Toggle
        checked={config.recv_enabled !== false}
        onChange={(v) => setConfig({ ...config, recv_enabled: v })}
        label="Принимать входящие письма"
      />
      <div style={{ height: 8 }} />
      <Toggle
        checked={config.send_enabled !== false}
        onChange={(v) => setConfig({ ...config, send_enabled: v })}
        label="Отправлять ответы по email"
      />
    </>
  );
}

function SmsWizard({
  step,
  creds,
  setCreds,
  channelName,
  setChannelName,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
  channelName: string;
  setChannelName: (n: string) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>SMS.ru</h4>
        <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          api_id — в личном кабинете SMS.ru → «Настройки» → «API». Используется для
          отправки SMS, например ответа клиенту, оставившему телефон в форме.
        </p>
        <Field label="Название канала" required>
          <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} />
        </Field>
        <Field label="api_id" required>
          <Input
            type="password"
            value={creds.api_id ?? ''}
            onChange={(e) => setCreds({ ...creds, api_id: e.target.value })}
          />
        </Field>
        <Field label="Имя отправителя" hint="одобренный sender id в SMS.ru; можно оставить пустым">
          <Input
            placeholder="MyShop"
            value={creds.from ?? ''}
            onChange={(e) => setCreds({ ...creds, from: e.target.value })}
          />
        </Field>
        <div style={{ height: 8 }} />
        <TestConnectionButton type="sms" creds={creds} label="Проверить api_id" />
      </>
    );
  return (
    <>
      <h4>Готово</h4>
      <p style={{ color: 'var(--inbx-text-tertiary)' }}>
        Канал SMS будет доступен для исходящих сообщений. Приём входящих SMS работает
        только при аренде номера в SMS.ru с настроенным callback.
      </p>
    </>
  );
}

function MarketplaceWizard({
  step,
  platform,
  creds,
  setCreds,
}: {
  step: number;
  platform: string;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>API credentials {platform}</h4>
        <Field label={`API-ключ ${platform}`} required>
          <Input
            type="password"
            value={creds.api_key ?? ''}
            onChange={(e) => setCreds({ ...creds, api_key: e.target.value })}
          />
        </Field>
        {platform === 'Ozon' && (
          <Field label="Client-ID" required>
            <Input
              value={creds.client_id ?? ''}
              onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
            />
          </Field>
        )}
        <TestConnectionButton
          type={platform === 'Ozon' ? 'ozon' : 'wildberries'}
          creds={creds}
          label="Проверить ключ"
        />
      </>
    );
  if (step === 2)
    return (
      <>
        <h4>Что собирается</h4>
        <p style={{ lineHeight: 1.6 }}>
          Подтягиваем <strong>неотвеченные отзывы и вопросы</strong> к товарам {platform} — каждый
          становится тикетом во «Входящих», ответ из панели уходит обратно на площадку.
        </p>
        <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          Уже отвеченные не тянем, чтобы не плодить дубли.
        </p>
      </>
    );
  return (
    <>
      <h4>Готово</h4>
      <p style={{ lineHeight: 1.6 }}>
        Нажмите <strong>«Завершить»</strong>. Новые отзывы и вопросы забираются автоматически
        примерно раз в 5 минут — отдельно ничего настраивать не нужно.
      </p>
    </>
  );
}

function AvitoWizard({
  step,
  creds,
  setCreds,
}: {
  step: number;
  creds: Record<string, string>;
  setCreds: (c: Record<string, string>) => void;
}): JSX.Element {
  if (step === 1)
    return (
      <>
        <h4>API Авито</h4>
        <p style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
          Данные из личного кабинета Avito → «Настройки» → «Авито API» (client_id,
          client_secret, идентификатор пользователя).
        </p>
        <Field label="Client ID" required>
          <Input
            value={creds.client_id ?? ''}
            onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
          />
        </Field>
        <Field label="Client Secret" required>
          <Input
            type="password"
            value={creds.client_secret ?? ''}
            onChange={(e) => setCreds({ ...creds, client_secret: e.target.value })}
          />
        </Field>
        <Field label="User ID" required>
          <Input
            placeholder="123456789"
            value={creds.user_id ?? ''}
            onChange={(e) => setCreds({ ...creds, user_id: e.target.value })}
          />
        </Field>
        <TestConnectionButton type="avito" creds={creds} />
      </>
    );
  return (
    <>
      <h4>Готово</h4>
      <p style={{ color: 'var(--inbx-text-tertiary)' }}>
        Сообщения и вопросы из чатов Avito будут забираться автоматически каждые
        несколько минут.
      </p>
    </>
  );
}
